import { csx, IconCaretRight, Text } from '@vtex/admin-ui'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { useHistory, useLocation } from 'react-router'

import {
  DrawerBody,
  DrawerSelectionItem,
  DrawerTitle,
} from '../../../../ui/components'
import { PaymentMethodIcon } from './PaymentMethodIcon'
import { PaymentsMethodsListContentBestInstallment } from './PaymentsMethodsListContentBestInstallment'
import type { PaymentsMethodsListContentStep_PaymentOptionConnection$key } from './__generated__/PaymentsMethodsListContentStep_PaymentOptionConnection.graphql'

const paymentOptionConnectionFragment = graphql`
  fragment PaymentsMethodsListContentStep_PaymentOptionConnection on PaymentOptionConnection {
    edges {
      node {
        paymentMethod {
          id
          groupName
          name
        }
        installments {
          edges {
            __typename
          }
        }
        ...PaymentsMethodsListContentBestInstallment_PaymentOption
      }
    }
  }
`

export type PaymentsMethodsListContentStepProps = {
  paymentOptionConnectionRef: PaymentsMethodsListContentStep_PaymentOptionConnection$key
}

export const PaymentsMethodsListContentStep = ({
  paymentOptionConnectionRef,
}: PaymentsMethodsListContentStepProps) => {
  const history = useHistory()
  const { search } = useLocation()

  const paymentOptionConnection = useSuspenseFragment(
    paymentOptionConnectionFragment,
    paymentOptionConnectionRef
  )

  const handlePaymentOptionClick = (paymentMethodId: string) => () => {
    history.push({
      search,
      hash: `#/payments-methods-drawer/${paymentMethodId}`,
    })
  }

  return (
    <>
      <DrawerTitle>
        <FormattedMessage id="pdp.payment.payments-drawer.title" />
      </DrawerTitle>
      <DrawerBody>
        <Text
          variant="action2"
          tone="secondary"
          className={csx({
            display: 'block',
          })}
        >
          <FormattedMessage id="pdp.payment.payments-drawer.subtitle" />
        </Text>
        <div
          className={csx({
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '$space-3',
            marginTop: '$space-8',
          })}
        >
          {paymentOptionConnection.edges.map((paymentOption) => {
            const hasMultipleInstallments =
              paymentOption.node.installments.edges.length > 1

            return (
              <DrawerSelectionItem
                key={paymentOption.node.paymentMethod.id}
                data-testid="payments-methodd-list-item"
                className={csx({
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center',
                })}
                onClick={
                  hasMultipleInstallments
                    ? handlePaymentOptionClick(
                        paymentOption.node.paymentMethod.id
                      )
                    : undefined
                }
              >
                <div>
                  <PaymentMethodIcon
                    groupName={paymentOption.node.paymentMethod.groupName}
                    className={csx({ color: '$blue40' })}
                  />
                  <Text
                    variant="action1"
                    className={csx({
                      display: 'block',
                      marginTop: '$space-5',
                    })}
                  >
                    {paymentOption.node.paymentMethod.name}
                  </Text>
                  <Text
                    variant="action2"
                    className={csx({
                      display: 'block',
                      marginTop: '$space-1',
                    })}
                  >
                    <PaymentsMethodsListContentBestInstallment
                      paymentOptionConnectionRef={paymentOption.node}
                    />
                  </Text>
                </div>
                {hasMultipleInstallments && (
                  <div
                    className={csx({
                      display: 'flex',
                    })}
                  >
                    <IconCaretRight size="small" />
                  </div>
                )}
              </DrawerSelectionItem>
            )
          })}
        </div>
      </DrawerBody>
    </>
  )
}
